import React, { useEffect, useMemo, useState } from 'react'

import { Button, Checkbox, Form, Header, Icon, Label, Message, Table } from 'semantic-ui-react'

import Data, { IProject, ISubmissionWebhookDelivery, ISubmissionWebhookEndpoint } from '../../api/Data'

const SubmissionWebhooks = () => {
  const [projects, setProjects] = useState<Array<Pick<IProject, 'id' | 'slug' | 'name'>>>([])
  const [projectSlug, setProjectSlug] = useState('')
  const [endpoint, setEndpoint] = useState<ISubmissionWebhookEndpoint | null>(null)
  const [deliveries, setDeliveries] = useState<ISubmissionWebhookDelivery[]>([])
  const [url, setUrl] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [revealedSecret, setRevealedSecret] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ error?: boolean; text: string } | null>(null)

  useEffect(() => {
    const loadProjects = async () => {
      const { data, error } = await Data.getSubmissionWebhookProjects()
      if (error) {
        setMessage({ error: true, text: 'Could not load projects.' })
        return
      }
      const availableProjects = data || []
      setProjects(availableProjects)
      if (availableProjects.length) setProjectSlug(availableProjects[0].slug)
    }
    loadProjects()
  }, [])

  const loadWebhook = async (slug: string) => {
    if (!slug) return
    setLoading(true)
    setMessage(null)
    setRevealedSecret('')
    const [{ data, error }, deliveryResult] = await Promise.all([
      Data.getSubmissionWebhook(slug),
      Data.getSubmissionWebhookDeliveries(slug),
    ])
    if (error || deliveryResult.error) {
      setMessage({ error: true, text: 'Could not load the submission webhook.' })
    } else {
      setEndpoint(data)
      setUrl(data?.url || '')
      setEnabled(data?.enabled !== false)
      setDeliveries(deliveryResult.data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    loadWebhook(projectSlug)
  }, [projectSlug])

  const projectOptions = useMemo(
    () => projects.map((project) => ({ key: project.slug, text: project.name, value: project.slug })),
    [projects]
  )

  const save = async (rotateSecret = false) => {
    if (!projectSlug || !url.trim()) return
    setLoading(true)
    setMessage(null)
    const { data, error } = await Data.updateSubmissionWebhook(projectSlug, {
      url: url.trim(),
      enabled,
      rotateSecret,
    })
    if (error || !data) {
      setMessage({ error: true, text: error?.response?.data?.message || 'Could not save the webhook.' })
    } else {
      setEndpoint(data)
      setRevealedSecret(data.secret || '')
      setMessage({
        text: data.secret
          ? 'Webhook saved. Copy the signing secret now; it will not be shown again.'
          : 'Webhook saved.',
      })
    }
    setLoading(false)
  }

  const remove = async () => {
    if (!projectSlug || !window.confirm('Disconnect this submission webhook?')) return
    setLoading(true)
    const { error } = await Data.deleteSubmissionWebhook(projectSlug)
    if (error) {
      setMessage({ error: true, text: 'Could not disconnect the webhook.' })
    } else {
      setEndpoint(null)
      setUrl('')
      setEnabled(true)
      setDeliveries([])
      setRevealedSecret('')
      setMessage({ text: 'Webhook disconnected.' })
    }
    setLoading(false)
  }

  const copySecret = async () => {
    await navigator.clipboard.writeText(revealedSecret)
    setMessage({ text: 'Signing secret copied.' })
  }

  const retry = async (deliveryId: string) => {
    const { error } = await Data.retrySubmissionWebhookDelivery(projectSlug, deliveryId)
    if (error) {
      setMessage({ error: true, text: 'Could not retry the delivery.' })
      return
    }
    setMessage({ text: 'Delivery queued for retry.' })
    await loadWebhook(projectSlug)
  }

  return (
    <>
      <Header as="h3">
        Submission webhooks
        <Header.Subheader>
          Send a signed <code>submission.received</code> event to Make, Zapier or your own API after a submission is
          accepted.
        </Header.Subheader>
      </Header>

      {message && <Message negative={message.error} positive={!message.error} content={message.text} />}

      <Form loading={loading}>
        <Form.Select
          label="Project"
          placeholder="Choose a project"
          options={projectOptions}
          value={projectSlug}
          onChange={(_, data) => setProjectSlug(String(data.value || ''))}
        />
        <Form.Input
          required
          label="Webhook URL"
          placeholder="https://hooks.zapier.com/..."
          value={url}
          onChange={(_, data) => setUrl(data.value)}
        />
        <Form.Field>
          <Checkbox label="Enabled" checked={enabled} onChange={(_, data) => setEnabled(Boolean(data.checked))} />
        </Form.Field>
        <Button primary disabled={!projectSlug || !url.trim()} onClick={() => save(false)}>
          {endpoint ? 'Save webhook' : 'Connect webhook'}
        </Button>
        {endpoint && (
          <>
            <Button basic onClick={() => save(true)}>
              Rotate signing secret
            </Button>
            <Button basic color="red" onClick={remove}>
              Disconnect
            </Button>
          </>
        )}
      </Form>

      {revealedSecret && (
        <Message>
          <Message.Header>Signing secret (shown once)</Message.Header>
          <code style={{ overflowWrap: 'anywhere' }}>{revealedSecret}</code>
          <Button size="mini" basic icon labelPosition="left" onClick={copySecret} style={{ marginLeft: '1rem' }}>
            <Icon name="copy" />
            Copy
          </Button>
        </Message>
      )}

      {endpoint && (
        <>
          <Header as="h4">Recent deliveries</Header>
          <Button size="mini" basic onClick={() => loadWebhook(projectSlug)}>
            Refresh
          </Button>
          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Submission ID</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Attempts</Table.HeaderCell>
                <Table.HeaderCell>HTTP</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {!deliveries.length && (
                <Table.Row>
                  <Table.Cell colSpan="5">No delivery attempts yet.</Table.Cell>
                </Table.Row>
              )}
              {deliveries.map((delivery) => (
                <Table.Row key={delivery.id}>
                  <Table.Cell>
                    <code>{delivery.submissionId}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <Label
                      color={
                        delivery.status === 'delivered' ? 'green' : delivery.status === 'failed' ? 'red' : 'yellow'
                      }
                    >
                      {delivery.status}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>{delivery.attempts}</Table.Cell>
                  <Table.Cell>{delivery.responseStatus || '—'}</Table.Cell>
                  <Table.Cell>
                    {delivery.status !== 'delivered' && (
                      <Button size="mini" basic onClick={() => retry(delivery.id)}>
                        Retry
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </>
  )
}

export default SubmissionWebhooks
